import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../app/Store";
import { LoadingState } from "../app/LoadingState";
import { beginRequest, completeRequest, rejectRequest, RequestState } from "../app/store/RequestState";
import { getSiteDesignMetadata } from "./TrackersAPI";
import { SiteDesignMetadata } from "./TrackersModels";

interface SiteDesignEntry extends RequestState {
  data?: SiteDesignMetadata;
  receivedAt?: number;
}
type SiteDesignState = Record<string, SiteDesignEntry>;
const initialState: SiteDesignState = {};

function pruneSettledSiteDesigns(state: SiteDesignState, currentSiteId: string) {
  // Pending entries must keep their request ownership until their response settles.
  const settledSites = Object.keys(state).filter(
    (siteId) => state[siteId].loadingState !== LoadingState.Pending
  );
  const otherSites = settledSites
    .filter((siteId) => siteId !== currentSiteId)
    .sort((a, b) => (state[a].receivedAt ?? 0) - (state[b].receivedAt ?? 0));
  for (const siteId of otherSites.slice(0, Math.max(0, settledSites.length - 10))) {
    delete state[siteId];
  }
}

export const fetchSiteDesignMetadata = createAsyncThunk(
  "siteDesignMetadata/fetch",
  async (siteId: string, { signal }) => ({
    data: await getSiteDesignMetadata(siteId, signal),
    receivedAt: Date.now(),
  }),
  {
    condition: (siteId, { getState }) =>
      Boolean(siteId) &&
      (getState() as RootState).siteDesignMetadata?.[siteId]?.loadingState !== LoadingState.Pending,
  }
);

const slice = createSlice({
  name: "siteDesignMetadata",
  initialState,
  reducers: {},
  extraReducers: (builder) =>
    builder
      .addCase(fetchSiteDesignMetadata.pending, (state, action) => {
        const current = (state[action.meta.arg] ??= { loadingState: LoadingState.None });
        beginRequest(current, action.meta.requestId);
      })
      .addCase(fetchSiteDesignMetadata.fulfilled, (state, action) => {
        const current = state[action.meta.arg];
        if (!current || !completeRequest(current, action.meta.requestId)) {
          return;
        }
        Object.assign(current, action.payload);
        // Geometry is large; retain only ten settled sites and reload older sites on demand.
        pruneSettledSiteDesigns(state, action.meta.arg);
      })
      .addCase(fetchSiteDesignMetadata.rejected, (state, action) => {
        const current = state[action.meta.arg];
        if (current && rejectRequest(current, action)) {
          pruneSettledSiteDesigns(state, action.meta.arg);
        }
      }),
});
export const siteDesignMetadataReducer = slice.reducer;
